import * as React from 'react';

/** Default periods window: ~3 months ahead of the anchor date. */
export const AVAILABILITY_MONTHS_AHEAD_DEFAULT = 3;
/** Each "load more" activation extends the window by this many months. */
export const AVAILABILITY_MONTHS_AHEAD_STEP = 3;
/** The shared window never extends past this many months ahead. */
export const AVAILABILITY_MONTHS_AHEAD_MAX = 12;

export interface AvailabilityLoadMoreWindow {
  /** Current shared periods window, passed straight into buildPeriods()'s horizonMonths param. */
  monthsAhead: number;
  /** False once the window has reached the 12-month cap — hides the load-more affordance. */
  canLoadMore: boolean;
  /** Extends the window by +3 months, clamped to the 12-month cap. No-op once capped. */
  loadMore: () => void;
}

/**
 * Owns the shared "load more periods" window for the inventory availability columns
 * (see proposal.md: "Manual load more in +3-month steps, capped at 12 months"). Lives
 * at the route level (not per-row) so vertically-loaded rows inherit the same window —
 * see the "Global shared timeline" decision in the change proposal.
 */
export function useAvailabilityLoadMoreWindow(): AvailabilityLoadMoreWindow {
  const [monthsAhead, setMonthsAhead] = React.useState(AVAILABILITY_MONTHS_AHEAD_DEFAULT);

  const loadMore = React.useCallback(() => {
    setMonthsAhead((prev) =>
      Math.min(prev + AVAILABILITY_MONTHS_AHEAD_STEP, AVAILABILITY_MONTHS_AHEAD_MAX),
    );
  }, []);

  return {
    monthsAhead,
    canLoadMore: monthsAhead < AVAILABILITY_MONTHS_AHEAD_MAX,
    loadMore,
  };
}
